import React from "react";
import COLORS from "../Helpers/colors";
import { useIsSmallScreen } from "../hooks/useIsSmallScreen";

interface LocationPinProps {
    /** The point the pin's tip marks, relative to the positioned parent. */
    left: string;
    top: string;
    /** Pin height in px (70% of it on phones, where images are small); the width follows the shape's proportions. */
    size?: number;
    /** Extra scale around the tip, e.g. 1 / zoom to keep the pin's size in a zoomed photo. */
    scale?: number;
    /** Accessible name; without it the pin is decorative (aria-hidden). */
    label?: string;
}

// A map pin (teardrop with a hole) whose tip sits on the marked point. Larger
// than Marker's dot, for full-size satellite images where a dot gets lost.
// Shape: the path from the design's marker-red-2.svg, in its own coordinates
// (the export's transforms only scaled and moved it). The viewBox is the
// path's bounds, so the tip (213,426) is the bottom centre and
// translate(-50%, -100%) puts it on the point. The white disc fills the hole;
// the white border is painted under the fill, so only its outer half shows and
// the red tip stays on the point.
const PIN_WIDTH = 267.428;
const PIN_HEIGHT = 425.964;
const BORDER_PX = 2;

const LocationPin: React.FC<LocationPinProps> = ({ left, top, size = 56, scale = 1, label }) => {
    const height = useIsSmallScreen() ? Math.round(size * 0.7) : size;
    return (
        <svg
            className="location-pin"
            viewBox={`79.268 0 ${PIN_WIDTH} ${PIN_HEIGHT}`}
            width={(height * PIN_WIDTH) / PIN_HEIGHT}
            height={height}
            {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
            style={{
                position: 'absolute',
                left,
                top,
                // Scaling about the bottom centre keeps the tip on the point
                transform: `translate(-50%, -100%)${scale !== 1 ? ` scale(${scale})` : ''}`,
                transformOrigin: '50% 100%',
                overflow: 'visible',
                filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.5))',
                zIndex: 10,
                pointerEvents: 'none',
            }}
        >
            <circle cx="212.98" cy="136.62" r="70" fill="white" />
            <path
                stroke="white"
                strokeWidth={(2 * BORDER_PX * PIN_HEIGHT) / height}
                strokeLinejoin="round"
                paintOrder="stroke"
                d="M213.285,0L212.677,0C139.114,0 79.268,59.826 79.268,133.361C79.268,181.563 101.22,245.178 144.514,322.442C176.612,379.723 209.16,423.594 209.486,424.03C210.392,425.247 211.82,425.964 213.333,425.964C213.376,425.964 213.42,425.964 213.463,425.962C215.024,425.919 216.465,425.12 217.331,423.819C217.652,423.333 249.968,374.532 281.848,314.843C324.878,234.28 346.696,173.219 346.696,133.361C346.693,59.825 286.846,0 213.285,0ZM274.865,136.62C274.865,170.744 247.104,198.504 212.98,198.504C178.857,198.504 151.096,170.743 151.096,136.62C151.096,102.497 178.857,74.736 212.98,74.736C247.104,74.736 274.865,102.497 274.865,136.62Z"
                fill={COLORS.locationRed}
            />
        </svg>
    );
};

export default LocationPin;
